import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../lib/i18n";
import { useAccount, formatApiError } from "../../lib/account";

export const AccountModal = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const a = t.account;
  const { login, register } = useAccount();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isReg = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const user = isReg
        ? await register(form.name, form.email, form.password)
        : await login(form.email, form.password);
      toast.success(`${a.greeting}, ${user.name?.split(" ")[0] || user.email}`);
      onOpenChange(false);
      setForm({ name: "", email: "", password: "" });
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-charcoal border-white/10 text-offwhite sm:max-w-md" data-testid="account-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{isReg ? a.join : a.welcome}</DialogTitle>
          <DialogDescription className="text-white/50">
            {isReg ? a.noAccount : a.haveAccount}{" "}
            <button
              type="button"
              onClick={() => { setMode(isReg ? "login" : "register"); setErr(""); }}
              className="text-lime2 hover:underline font-medium"
              data-testid="account-switch-mode"
            >
              {isReg ? a.switchToLogin : a.switchToRegister}
            </button>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          {isReg && (
            <Input value={form.name} onChange={set("name")} placeholder={a.name} required
                   data-testid="account-name" className="bg-black/40 border-white/15 h-12" />
          )}
          <Input type="email" value={form.email} onChange={set("email")} placeholder={a.email} required
                 data-testid="account-email" className="bg-black/40 border-white/15 h-12" />
          <Input type="password" value={form.password} onChange={set("password")} placeholder={a.password} required
                 minLength={6} data-testid="account-password" className="bg-black/40 border-white/15 h-12" />

          {err && <p className="text-sm text-red-400" data-testid="account-error">{err}</p>}

          <Button type="submit" disabled={busy} data-testid="account-submit"
                  className="w-full h-12 rounded-full bg-lime2 text-charcoal hover:bg-white font-semibold">
            {busy ? <><Loader2 size={16} className="animate-spin mr-2" /> {isReg ? a.registering : a.loggingIn}</>
                  : (isReg ? a.registerCta : a.loginCta)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
